import { AuthRoutes } from 'routes';

export const isSecureUploadPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '') === AuthRoutes.SecureUploadRequest;

/** True for a full or relative URL whose path is the upload page, whatever query or fragment follows. */
export const isSecureUploadUrl = (url: string): boolean => {
  try {
    return isSecureUploadPath(new URL(url, window.location.origin).pathname);
  } catch {
    return false;
  }
};

export const isOnSecureUploadPage = (): boolean =>
  typeof window !== 'undefined' && isSecureUploadPath(window.location.pathname);

const TOKEN_GLOBAL = '__theraSecureUploadToken';

/**
 * Runs inline in the document head, before Next.js and every analytics SDK boot. Those all read the
 * URL at start-up: PostHog stores it in localStorage, and Next's router can write it back into
 * history. Taking the token off the URL here means none of them ever sees it. A mistyped case still
 * loses its fragment, so the 404 page cannot leak it, but only the real path hands the token on.
 */
export const SECURE_UPLOAD_EARLY_SCRIPT = `(function(){try{var l=window.location,p=l.pathname.replace(/\\/+$/,''),r=${JSON.stringify(
  AuthRoutes.SecureUploadRequest,
)};if(!l.hash||p.toLowerCase()!==r)return;if(p===r)window.${TOKEN_GLOBAL}=l.hash.slice(1);window.history.replaceState(null,'',l.pathname+l.search);}catch(e){}})();`;

let heldToken: string | null | undefined;

const takeTokenFromAddressBar = (): string | null => {
  const token = window.location.hash.replace(/^#/, '') || null;
  if (token) window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return token;
};

/**
 * The link token for this page load, taken once. Held at module level because a provider above the
 * page can remount it, and the token is already gone from the URL by then. Memory only: a reload
 * forgets it, and the person opens the link again.
 */
export const takeSecureUploadToken = (): string | null => {
  if (heldToken !== undefined) return heldToken;
  const handoff = window as unknown as Record<string, unknown>;
  const early = typeof handoff[TOKEN_GLOBAL] === 'string' ? (handoff[TOKEN_GLOBAL] as string) : null;
  delete handoff[TOKEN_GLOBAL];
  // The address bar holds a token only if the early script did not run, or a link was opened again
  // before the page was ready. Either way it is the newer one.
  heldToken = takeTokenFromAddressBar() || early;
  return heldToken;
};

/**
 * Opening a link in a tab already on this page changes only the fragment, so the page does not
 * reload and the early script never sees it. Takes the new token off the address bar instead.
 */
export const onSecureUploadLinkOpened = (listener: (token: string) => void): (() => void) => {
  const handleHashChange = () => {
    const token = takeTokenFromAddressBar();
    if (!token) return;
    heldToken = token;
    listener(token);
  };
  window.addEventListener('hashchange', handleHashChange);
  return () => window.removeEventListener('hashchange', handleHashChange);
};

// Belt and braces behind the early script. Matched anywhere in a string, so a URL quoted inside an
// error message or a stack is caught as well as a bare view URL, with or without a query first.
const TOKEN_FRAGMENT = new RegExp(`(${AuthRoutes.SecureUploadRequest}/?(?:\\?[^#\\s"'<>]*)?)#[^\\s"'<>]*`, 'gi');

export const stripSecureUploadFragment = (text: string): string => text.replace(TOKEN_FRAGMENT, '$1');

const scrubInPlace = (value: unknown, seen: WeakSet<object>): void => {
  if (!value || typeof value !== 'object' || seen.has(value)) return;
  seen.add(value);
  const record = value as Record<string, unknown>;
  Object.keys(record).forEach((key) => {
    const inner = record[key];
    if (typeof inner === 'string') {
      const clean = stripSecureUploadFragment(inner);
      if (clean === inner) return;
      try {
        record[key] = clean;
      } catch {
        // A frozen field cannot be rewritten; the early script means it should never hold a token.
      }
    } else {
      scrubInPlace(inner, seen);
    }
  });
};

/**
 * Rewrites every string in an analytics event in place, which is what Datadog's beforeSend requires.
 * Off the upload page no token exists, so it does nothing there.
 */
export const scrubSecureUploadToken = <T>(event: T): T => {
  if (isOnSecureUploadPage()) scrubInPlace(event, new WeakSet());
  return event;
};
